import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { HEADER, NAVBAR } from 'src/config';
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';
import { cn } from 'src/@/lib/utils';
import ChatInternoGlobalSidebar from 'src/sections/@chats/chatInterno/ChatInternoGlobalSidebar';
import { clearSessionStorageItem } from 'src/services/sessionStorage';

import UpdateBanner from 'src/components/UpdateBanner';
import InadimplenciaBanner, { BANNER_HEIGHT } from 'src/components/InadimplenciaBanner';
import useInadimplencia from 'src/hooks/useInadimplencia';
import DashboardNavbar from './DashboardNavbar';
import MainSidebar from './MainSidebar';

// ----------------------------------------------------------------------

export default function ChatLayout() {
  const { collapseClick } = useCollapseDrawer();
  const { pathname } = useLocation();
  const isChatInternoPage = pathname.startsWith('/chats/interno');
  const isFullscreenEditor = pathname.includes('/chatbots/builder/');
  const { ativo: inadimplenciaAtivo } = useInadimplencia();
  const topOffset = inadimplenciaAtivo ? BANNER_HEIGHT : 0;

  const [layoutMode, setLayoutMode] = useState(() => {
    return localStorage.getItem('layoutMode') || 'navbar';
  });

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('chatInternoSidebarOpen');
      return saved !== null ? JSON.parse(saved) : true;
    } catch { return true; }
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const newMode = localStorage.getItem('layoutMode') || 'navbar';
      setLayoutMode(newMode);

      try {
        const saved = localStorage.getItem('chatInternoSidebarOpen');
        setSidebarOpen(saved !== null ? JSON.parse(saved) : true);
      } catch { /* ignore */ }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('layoutModeChange', handleStorageChange);
    window.addEventListener('chatInternoSidebarToggle', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('layoutModeChange', handleStorageChange);
      window.removeEventListener('chatInternoSidebarToggle', handleStorageChange);
    };
  }, []);

  // Ao entrar na página de chat interno, limpa o minichat
  // Ao sair, também limpa para evitar minichat reabrir
  useEffect(() => {
    if (isChatInternoPage) {
      clearSessionStorageItem('chatInternoMiniId');
    }
    return () => {
      clearSessionStorageItem('chatInternoMiniId');
    };
  }, [isChatInternoPage]);

  const showSidebarMargin = sidebarOpen && !isChatInternoPage && !isFullscreenEditor;

  if (layoutMode === 'sidebar') {
    return (
      <>
        <InadimplenciaBanner />
        <div
          className="flex bg-neutral-50 m-0 min-h-screen overflow-hidden"
          style={{ paddingTop: topOffset }}
        >
          <UpdateBanner />
          <MainSidebar />
          <main
            className={cn(
              'grow w-full h-screen overflow-hidden',
              'transition-[margin-right] duration-200',
              showSidebarMargin ? 'mr-14' : 'mr-0'
            )}
          >
            <Outlet />
          </main>
          <ChatInternoGlobalSidebar />
        </div>
      </>
    );
  }

  return (
    <>
      <InadimplenciaBanner />
      <div
        className="m-0 bg-neutral-50 overflow-hidden lg:flex h-screen"
        style={{ paddingTop: topOffset }}
      >
        <UpdateBanner />
        <DashboardNavbar topOffset={topOffset} />
        <main
          className={cn(
            'grow w-full h-full overflow-hidden pt-14',
            'transition-[margin-right] duration-200',
            showSidebarMargin ? 'mr-14' : 'mr-0'
          )}
        >
          <Outlet />
        </main>
        <ChatInternoGlobalSidebar />
      </div>
    </>
  );
}
